import { useMemo } from 'react';
import {
  assetTypes,
  macroAssetTypes,
  subAssetTypeFields,
  subAssetTypes
} from '../../../../consts/assetTypes';
import {
  getCellWidths,
  getColumnByType,
  getRowHeight,
  getTextHeightForParagraph,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import { useSearchParams } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { isEsmaTableDisabledForFund } from '../utils/visibleSections';

const tabsKey = {
  long_value: 'Long value',
  short_value: 'Short value',
  gross_value: 'Gross value'
};

const orderNumber = {
  long_value: '123',
  short_value: '124',
  gross_value: '122'
};

// Utility functions for cell dimensions
const getCellWidth = (isMultiFunds, isComparisonReport, fundSize, contentWidth) => {
  if (!isMultiFunds) return contentWidth / 2;
  if (isComparisonReport) return 300;
  return fundSize <= 3 ? contentWidth / (fundSize + 1) : 300;
};

const getLabelWidth = (isMultiFunds, isComparisonReport, fundSize, contentWidth) => {
  if (!isMultiFunds) return contentWidth / 2;
  if (isComparisonReport) return 200;
  return fundSize <= 3 ? contentWidth / (fundSize + 1) : 200;
};

const getHeaderHeight = (isMultiFunds, text, labelWidth) => {
  if (!isMultiFunds) return 40;
  return getTextHeightForParagraph(text || '', labelWidth);
};

export default function useIndividualExposures({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds
}) {
  const [searchParams] = useSearchParams();
  const currentDataKey = searchParams.get('fund_id');
  const getFundExposureRows = fund => fund?.fund_data?.INDIVIDUAL_EXPOSURES || [];
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isEsmaTableDisabledForFund(fund, 'INDIVIDUAL_EXPOSURES');
  };
  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const onChange = value => {
    const changes = value[0];
    const _fund = isMultiFunds
      ? JSON.parse(JSON.stringify(getFundExposureRows(funds[changes.newCell.fundId])))
      : JSON.parse(JSON.stringify(data));
    const index = _fund.findIndex(item => item.sub_asset_type === changes.newCell.sub_asset_type);

    if (index < 0) return;

    _fund[index][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });

    onChangeFund('INDIVIDUAL_EXPOSURES', _fund, changes.newCell.fundId);
  };

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );
  const tables = useMemo(() => {
    if (!data) return [];

    let amountRow = 76;

    const groupedData = {};

    Object.keys(macroAssetTypes).forEach(key => {
      groupedData[key] = {};
    });

    Object.keys(assetTypes).forEach(key => {
      const macroKey = key.split('_')[0];
      if (groupedData[macroKey]) {
        if (key !== 'DER_IRD') {
          groupedData[macroKey][key] = {};
        }
      }
    });

    Object.keys(subAssetTypes).forEach(key => {
      const assetKey = key.split('_').slice(0, 2).join('_');
      const macroKey = key.split('_')[0];

      if (groupedData[macroKey] && groupedData[macroKey][assetKey]) {
        groupedData[macroKey][assetKey][key] = subAssetTypeFields[key];
      }

      if (key === 'DER_IRD_INTR') {
        groupedData['DER']['DER_FEX']['DER_IRD_INTR'] = subAssetTypeFields[key];
      }
    });

    const _fundSize = Object.keys(funds).length;

    const _tables = Object.keys(groupedData).map(macro_asset_type => {
      return {
        name: macroAssetTypes[macro_asset_type],
        tables: Object.keys(groupedData[macro_asset_type]).map(asset_type => {
          const assetType = groupedData[macro_asset_type][asset_type];

          amountRow = amountRow + Object.keys(assetType).length;
          // Move cellWidth and labelWidth calculation outside the sub_asset_type map
          const cellWidth = getCellWidth(isMultiFunds, isComparisonReport, _fundSize, contentWidth);
          const labelWidth = getLabelWidth(
            isMultiFunds,
            isComparisonReport,
            _fundSize,
            contentWidth
          );
          return {
            name: assetTypes[asset_type],
            tables: Object.keys(groupedData[macro_asset_type][asset_type]).map(
              (sub_asset_type, index) => {
                return {
                  columns: isMultiFunds
                    ? _fundSize <= 3 && !isComparisonReport
                      ? [
                          {
                            columnId: 0,
                            resizable: true,
                            width: 30
                          },
                          ...Array.from(Array(_fundSize + 1).keys()).map(index => ({
                            columnId: index + 1,
                            width: index === 0 ? labelWidth : cellWidth
                          }))
                        ]
                      : isComparisonReport
                        ? [
                            {
                              columnId: 0,
                              resizable: true,
                              width: 30
                            },
                            {
                              columnId: 1,
                              resizable: true,
                              width: 200
                            },
                            ...Object.keys(funds)
                              .map(item => [item, item.previous_fund])
                              .flat()
                              .map((_, index) => ({
                                columnId: index + 2,
                                width: 300
                              }))
                          ]
                        : [
                            {
                              columnId: 0,
                              resizable: true,
                              width: 30
                            },
                            {
                              columnId: 1,
                              resizable: true,
                              width: 200
                            },
                            ...Object.keys(funds).map((_, index) => ({
                              columnId: index + 2,
                              width: 300
                            }))
                          ]
                    : isComparisonReport
                      ? [
                          {
                            columnId: 0,
                            resizable: true,
                            width: 30
                          },
                          ...Array.from(Array(3).keys()).map(index => ({
                            columnId: index + 1,
                            width: contentWidth / 3
                          }))
                        ]
                      : [
                          {
                            columnId: 0,
                            resizable: true,
                            width: 30
                          },
                          ...Array.from(Array(2).keys()).map(index => ({
                            columnId: index + 1,
                            width: contentWidth / 2
                          }))
                        ],
                  rows: [
                    {
                      height: getHeaderHeight(
                        isMultiFunds,
                        subAssetTypes[sub_asset_type],
                        labelWidth
                      ),
                      rowId: 0,
                      cells: isMultiFunds
                        ? [
                            {
                              type: 'header',
                              text: ''
                            },
                            {
                              type: 'header',
                              text: subAssetTypes[sub_asset_type] || ''
                            },
                            ...(isComparisonReport
                              ? Object.values(funds)
                                  .map(item => [item, item.previous_fund])
                                  .flat()
                                  .map((fund, i) => ({
                                    type: 'header',
                                    text: fund?.fund_name || '',
                                    colspan: i % 2 === 0 ? 2 : undefined
                                  }))
                              : Object.values(funds).map(fund => ({
                                  type: 'header',
                                  text: fund.fund_name || ''
                                })))
                          ]
                        : [
                            {
                              type: 'header',
                              text: ''
                            },
                            {
                              type: 'header',
                              text: subAssetTypes[sub_asset_type] || ''
                            },
                            ...(isComparisonReport
                              ? [
                                  {
                                    type: 'header',
                                    text: `${funds[currentDataKey]?.previous_fund?.period}, ${funds[currentDataKey]?.previous_fund?.year}`
                                  },
                                  {
                                    type: 'header',
                                    text: `${funds[currentDataKey]?.period}, ${funds[currentDataKey]?.year}`
                                  }
                                ]
                              : [
                                  {
                                    type: 'header',
                                    text: ''
                                  }
                                ])
                          ]
                    },
                    ...(isComparisonReport && isMultiFunds
                      ? [
                          {
                            height: getHeaderHeight(
                              isMultiFunds,
                              subAssetTypes[sub_asset_type],
                              labelWidth
                            ),
                            rowId: 1,
                            cells: [
                              {
                                type: 'header',
                                text: ''
                              },
                              {
                                type: 'header',
                                text: ''
                              },
                              ...Object.values(funds)
                                .map(item => [item.previous_fund, item])
                                .flat()
                                .map(fund => ({
                                  type: 'header',
                                  text: `${fund?.period}, ${fund?.year}`
                                }))
                            ]
                          }
                        ]
                      : []),
                    ...Object.keys(subAssetTypeFields[sub_asset_type]).map((columnKey, index2) => {
                      const __value = data.find(val => val.sub_asset_type === sub_asset_type);
                      const number = amountRow - Object.keys(assetType).length + index + 1;
                      const __differenceList =
                        funds[currentDataKey]?.difference?.INDIVIDUAL_EXPOSURES;
                      const __difference = __differenceList
                        ? __differenceList.find(val => val.sub_asset_type === sub_asset_type)
                        : null;
                      const _previous_value = funds[
                        currentDataKey
                      ]?.previous_fund?.fund_data?.INDIVIDUAL_EXPOSURES?.find(
                        val => val.sub_asset_type === sub_asset_type
                      );
                      const cells = !isMultiFunds
                        ? [
                            {
                              type:
                                __value?.report_id &&
                                __value?.[columnKey] !== null &&
                                schema?.types?.[columnKey]?.has_drill_down_feature
                                  ? 'customHeader'
                                  : 'header',
                              text: tabsKey[columnKey] || '',
                              render: () => (
                                <CustomHeaderWithDrillDown
                                  reportId={__value?.report_id}
                                  fundId={__value.fund_id}
                                  columnName={columnKey}
                                  tableName="INDIVIDUAL_EXPOSURES"
                                  title={tabsKey[columnKey]}
                                  columnPrettyName={subAssetTypes[sub_asset_type]}
                                  tableId={__value?.id}
                                />
                              ),
                              style: {
                                'justify-content': 'flex-start',
                                'white-space': 'inherit'
                              }
                            },
                            ...(isComparisonReport
                              ? [
                                  {
                                    ...getColumnByType({
                                      type: schema?.types?.[columnKey]?.type,
                                      options: schema?.types?.[columnKey]?.options,
                                      key: columnKey,
                                      defaultValue: schema?.defaultValues?.[columnKey],
                                      index: 0,
                                      value: _previous_value,
                                      required: requiredFields.includes(columnKey),
                                      tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                      isStaticTemplate,
                                      isMultiFunds,
                                      isEsma: true,
                                      fundId: _previous_value?.fund_id,
                                      isSectionDisabled: isTableDisabledForFund(_previous_value),
                                      fundName: _previous_value?.fund_name,
                                      fieldOrder: number,
                                      nonEditable: isComparisonReport
                                    }),
                                    macro_asset_type,
                                    asset_type,
                                    sub_asset_type,
                                    isMultiFunds
                                  }
                                ]
                              : []),
                            {
                              ...getColumnByType({
                                type: schema?.types?.[columnKey]?.type,
                                options: schema?.types?.[columnKey]?.options,
                                key: columnKey,
                                defaultValue: schema?.defaultValues?.[columnKey],
                                index: 0,
                                value: __value,
                                required: requiredFields.includes(columnKey),
                                tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                isStaticTemplate,
                                isMultiFunds,
                                isEsma: true,
                                fundId: __value?.fund_id,
                                isSectionDisabled: isTableDisabledForFund(__value?.fund_id),
                                fundName: __value?.fund_name,
                                fieldOrder: number,
                                difference: isComparisonReport
                                  ? __difference?.[columnKey] || {}
                                  : null,
                                nonEditable: isComparisonReport
                              }),
                              macro_asset_type,
                              asset_type,
                              sub_asset_type
                            }
                          ]
                        : [
                            {
                              type: 'header',
                              text: tabsKey[columnKey] || '',
                              style: {
                                'justify-content': 'flex-start',
                                'white-space': 'inherit'
                              }
                            },
                            ...(isComparisonReport
                              ? Object.values(funds)
                                  .map(item => [item, item.previous_fund])
                                  .flat()
                                  .map((value, j) => {
                                    const _fundRows = getFundExposureRows(value);
                                    const _value = _fundRows.find(
                                      val => val.sub_asset_type === sub_asset_type
                                    );
                                    return {
                                      ...getColumnByType({
                                        type: schema?.types?.[columnKey]?.type,
                                        options: schema?.types?.[columnKey]?.options,
                                        key: columnKey,
                                        defaultValue: schema?.defaultValues?.[columnKey],
                                        index: 0,
                                        value: _value,
                                        required: requiredFields.includes(columnKey),
                                        tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                        isStaticTemplate,
                                        fundId: value?.fund_id,
                                        isSectionDisabled: isTableDisabledForFund(value?.fund_id),
                                        fundName: value?.fund_name,
                                        hasDrillDownFeature:
                                          !!_value?.report_id &&
                                          schema?.types?.[columnKey]?.has_drill_down_feature &&
                                          _value?.[columnKey] !== null,
                                        reportId: _value?.report_id,
                                        tableId: _value?.id,
                                        title: subAssetTypes[sub_asset_type] || '',
                                        isMultiFunds,
                                        isEsma: true,
                                        difference:
                                          j % 2 === 1 ? __difference?.[columnKey] || {} : null,
                                        fieldOrder: number,
                                        nonEditable: isComparisonReport
                                      }),
                                      macro_asset_type,
                                      asset_type,
                                      sub_asset_type
                                    };
                                  })
                              : Object.values(funds).map(value => {
                                  const _fundRows = getFundExposureRows(value);
                                  const _value = _fundRows.find(
                                    val => val.sub_asset_type === sub_asset_type
                                  );
                                  return {
                                    ...getColumnByType({
                                      type: schema?.types?.[columnKey]?.type,
                                      options: schema?.types?.[columnKey]?.options,
                                      key: columnKey,
                                      defaultValue: schema?.defaultValues?.[columnKey],
                                      index: 0,
                                      value: _value,
                                      required: requiredFields.includes(columnKey),
                                      tableName: 'INDIVIDUAL_EXPOSURES_' + sub_asset_type,
                                      isStaticTemplate,
                                      fundId: value.fund_id,
                                      isSectionDisabled: isTableDisabledForFund(value.fund_id),
                                      fundName: value.fund_name,
                                      hasDrillDownFeature:
                                        !!_value?.report_id &&
                                        schema?.types?.[columnKey]?.has_drill_down_feature &&
                                        _value?.[columnKey] !== null,
                                      reportId: _value?.report_id,
                                      tableId: _value?.id,
                                      title: subAssetTypes[sub_asset_type] || '',
                                      isMultiFunds,
                                      isEsma: true,
                                      fieldOrder: number,
                                      nonEditable: isComparisonReport
                                    }),
                                    macro_asset_type,
                                    asset_type,
                                    sub_asset_type
                                  };
                                }))
                          ];
                      const { labelWidth, valueWidth } = getCellWidths({
                        isMultiFunds,
                        fundSize: _fundSize,
                        contentWidth,
                        isComparisonReport
                      });

                      const valueCells = isMultiFunds
                        ? (isComparisonReport
                            ? Object.values(funds)
                                .map(item => [item, item.previous_fund])
                                .flat()
                            : Object.values(funds)
                          ).map(fund => {
                            const row = getFundExposureRows(fund).find(
                              item => item.sub_asset_type == sub_asset_type
                            );
                            return { value: '0', error: row?.errors?.[columnKey]?.msg || '' };
                          })
                        : isComparisonReport
                          ? [
                              {
                                value: '0',
                                error: _previous_value?.errors?.[columnKey]?.msg || ''
                              },
                              { value: '0', error: __value?.errors?.[columnKey]?.msg || '' }
                            ]
                          : [{ value: '0', error: __value?.errors?.[columnKey]?.msg || '' }];

                      const rowHeight = getRowHeight({
                        label: subAssetTypes[sub_asset_type],
                        labelWidth,
                        cells: valueCells,
                        valueWidth
                      });

                      return {
                        rowId: isComparisonReport && isMultiFunds ? index2 + 2 : index2 + 1,
                        height: rowHeight,
                        cells: [
                          {
                            type: 'header',
                            text: orderNumber[columnKey]
                          },
                          ...cells
                        ]
                      };
                    })
                  ]
                };
              }
            )
          };
        })
      };
    });

    return _tables;
  }, [data, isMultiFunds, contentWidth, funds, isComparisonReport]);

  return { onChange, tables };
}
